import React, { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import "../css/Rom.css";
import secureLocalStorage from "react-secure-storage";
import AWS from "aws-sdk";
import { getProgress } from "../Api";
import { romNotFoundMessage } from "../services/constants";
import romBg from "../rom-bg.png"; // adjust path if needed

// --- Improved Smoothing: OneEuroFilter for angles, EMA for landmarks ---
class OneEuroFilter {
  constructor(freq = 60, minCutoff = 0.2, beta = 0.6, dCutoff = 1.0) {
    this.freq = freq;
    this.minCutoff = minCutoff;
    this.beta = beta;
    this.dCutoff = dCutoff;
    this.lastTime = null;
    this.x = { value: null };
    this.dx = { value: null };
  }

  alpha(cutoff) {
    const tau = 1.0 / (2 * Math.PI * cutoff);
    const te = 1.0 / this.freq;
    return 1.0 / (1.0 + tau / te);
  }

  filter(value, timestamp) {
    const t = timestamp / 1000.0;
    if (this.lastTime === null) {
      this.lastTime = t;
      this.x.value = value;
      return value;
    }

    const te = t - this.lastTime;
    this.freq = 1.0 / te;
    this.lastTime = t;

    const dx = (value - this.x.value) * this.freq;
    const alphaD = this.alpha(this.dCutoff);
    this.dx.value = this.dx.value == null ? dx : this.dx.value + alphaD * (dx - this.dx.value);

    const cutoff = this.minCutoff + this.beta * Math.abs(this.dx.value);
    const alpha = this.alpha(cutoff);

    this.x.value = this.x.value + alpha * (value - this.x.value);
    return this.x.value;
  }
}

// --- Exponential Moving Average for landmark smoothing ---
function smoothLandmark(prev, curr, alpha = 0.5) {
  if (!prev) return { ...curr };
  return {
    x: prev.x * (1 - alpha) + curr.x * alpha,
    y: prev.y * (1 - alpha) + curr.y * alpha,
    z: prev.z * (1 - alpha) + curr.z * alpha,
    visibility: prev.visibility * (1 - alpha) + curr.visibility * alpha,
  };
}

const RomMN = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [angle, setAngle] = useState(null);
  const [tooClose, setTooClose] = useState(false);
  const [isCountdown, setIsCountdown] = useState(false);
  const [progress, setProgress] = useState(0);
  const [surgeryStatus, setSurgeryStatus] = useState(null);
  const [whichKnee, setWhichKnee] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [shareScreenshot, setShareScreenshot] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [imageSaved, setImageSaved] = useState(false);
  const [noDetection, setNoDetection] = useState(false);
  const [tips, setTips] = useState(true);
  const [updatedText, setUpdatedText] = useState("");
  const [backgroundColor, setBackgroundColor] = useState("#CAEEF9");
  const [isScreenshotBG, setIsScreenshotBG] = useState(false);
  const filterRef = useRef(new OneEuroFilter(60, 0.2, 0.6, 1.0));
  const angleBuffer = useRef([]);
  const BUFFER_SIZE = 30;

  const backgroundRef = useRef(null);

  useEffect(() => {
    const img = new Image();
    img.src = romBg;
    img.onload = () => {
      backgroundRef.current = img;
    };
  }, []);

  const prevLandmarksRef = useRef({ hip: null, knee: null, ankle: null });

  const [shareImageUrl, setShareImageUrl] = useState(null);
  const [shareSignedUrl, setShareSignedUrl] = useState(null);
  const [shareUploading, setShareUploading] = useState(false);

  const getPatientId = () => secureLocalStorage.getItem("patient_id");

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const response = await getProgress(getPatientId());
        secureLocalStorage.setItem("week_no", response.patient_info.week_no);
        secureLocalStorage.setItem("patient_info", response.patient_info);
        setWhichKnee(response.patient_info.which_knee);
      } catch (error) {
        console.error("Error fetching progress:", error);
      }
    };

    fetchProgress();
  }, []);

  useEffect(() => {
    const updateSurgeryStatus = () => {
      const patientInfo = secureLocalStorage.getItem("patient_info");
      const surgeryDate = new Date(patientInfo?.start_date);
      const typeStr = patientInfo?.is_preventive
        ? "Prevention"
        : surgeryDate > new Date()
        ? "Pre-Op"
        : "Post-Op";

      setSurgeryStatus(typeStr);
    };

    updateSurgeryStatus();
  }, []);

  AWS.config.update({
    accessKeyId: "AKIAQEIP3MK7YSCRON7B",
    secretAccessKey: "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",
    region: "ap-southeast-2",
  });

  const s3 = new AWS.S3();

  const getImagePath = (detectedAngle) => {
    const anglePaths = [
      { max: 20, path: "../rom-sprites/ROM1.png" },
      { max: 60, path: "../rom-sprites/ROM2.png" },
      { max: 83, path: "../rom-sprites/ROM3.png" },
      { max: 90, path: "../rom-sprites/ROM4.png" },
      { max: 105, path: "../rom-sprites/ROM5.png" },
      { max: 115, path: "../rom-sprites/ROM6.png" },
      { max: 134, path: "../rom-sprites/ROM7.png" },
    ];

    if (detectedAngle === null || !detectedAngle) return "../rom-sprites/ROM0.png";

    for (const { max, path } of anglePaths) {
      if (detectedAngle < max) return path;
    }

    return "../rom-sprites/ROM8.png";
  };

  const updateSavedText = (angle) => {
    const messages = [
      {
        condition: angle < 83,
        message: "You're getting there! Keep working towards 83° to climb stairs safely.",
      },
      {
        condition: angle >= 83 && angle <= 89,
        message: "Awesome! You can climb stairs now.",
      },
      {
        condition: angle === 90,
        message: "Great job! You've reached 90°, safe to descend stairs.",
      },
      {
        condition: angle >= 91 && angle <= 104,
        message: "You're almost there! Keep going.",
      },
      {
        condition: angle === 105,
        message: "Amazing! Standing up from a chair is easier.",
      },
      {
        condition: angle >= 106 && angle <= 114,
        message: "You're getting close to biking time.",
      },
      {
        condition: angle === 115,
        message: "Fantastic! You’re ready to ride a bike!",
      },
      {
        condition: angle >= 116 && angle <= 134,
        message: "Keep pushing, full flexion is right around the corner.",
      },
      { condition: angle === 135, message: "Full flexion achieved!" },
    ];

    const message = messages.find((msg) => msg.condition)?.message;
    if (message) setUpdatedText(message);
  };

  const uploadToS3 = async (imageData) => {
    const patientId = secureLocalStorage.getItem("patient_id");
    const timestamp = new Date().toISOString().replace(/[:.-]/g, "");
    const fileName = `rom-${patientId}-${timestamp}.png`;

    const params = {
      Bucket: "engivio-rom-uploads",
      Key: fileName,
      Body: Uint8Array.from(
        atob(imageData.replace(/^data:image\/\w+;base64,/, "")),
        (c) => c.charCodeAt(0)
      ),
      ContentEncoding: "base64",
      ContentType: "image/png",
    };

    try {
      const data = await s3.upload(params).promise();
      return data.Location;
    } catch (error) {
      console.error("Error uploading to S3:", error);
      return null;
    }
  };

  const saveData = async () => {
    const romValue = secureLocalStorage.getItem("last_angle_detected").replace("°", "");
    const imageUrl = await uploadToS3(screenshot);

    if (!imageUrl) {
      console.error("Failed to upload image to S3");
      return;
    }

    const data = {
      week_no: secureLocalStorage.getItem("week_no"),
      knee_flexion: 0,
      knee_extension: 0,
      hip_abduction: 0,
      hip_adduction: 0,
      hip_extension: 0,
      hip_flexion: 0,
      hip_rotation_ext: 0,
      hip_rotation_int: 0,
      patient_id: secureLocalStorage.getItem("patient_id"),
      rom_value: romValue,
      image: imageUrl,
    };
    console.log(data);
    try {
      const response = await fetch("https://api2.engiv.io/rom", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error("Failed to save data");
      }
      console.log("Data saved successfully");
    } catch (error) {
      console.error("Error saving data:", error);
    }
  };

  useEffect(() => {
    if (!window.Pose || !window.Camera) {
      console.error("MediaPipe Pose or Camera not loaded");
      return;
    }

    let poseDeleted = false;
    let cameraDeleted = false;

    let pose = null;
    let camera = null;

    try {
      pose = new window.Pose({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/pose@0.5/${file}`,
      });

      pose.setOptions({
        modelComplexity: 1,
        smoothLandmarks: true,
        minDetectionConfidence: 0.6,
        minTrackingConfidence: 0.6,
      });

      const onResults = (results) => {
        if (poseDeleted) return;
        const width = canvasElement.width;
        const height = canvasElement.height;

        canvasCtx.save();
        canvasCtx.clearRect(0, 0, width, height);
        canvasCtx.drawImage(results.image, 0, 0, width, height);

        const landmarks = results.poseLandmarks;
        const currentWhichKnee = whichKnee;

        const hipIndex = currentWhichKnee === "Left" ? 23 : 24;
        const kneeIndex = currentWhichKnee === "Left" ? 25 : 26;
        const ankleIndex = currentWhichKnee === "Left" ? 27 : 28;

        let hip = landmarks?.[hipIndex];
        let knee = landmarks?.[kneeIndex];
        let ankle = landmarks?.[ankleIndex];

        const ALPHA = 0.3;
        if (hip && knee && ankle) {
          hip = smoothLandmark(prevLandmarksRef.current.hip, hip, ALPHA);
          knee = smoothLandmark(prevLandmarksRef.current.knee, knee, ALPHA);
          ankle = smoothLandmark(prevLandmarksRef.current.ankle, ankle, ALPHA);
          prevLandmarksRef.current = { hip, knee, ankle };
        }

        const allVisible =
          hip && knee && ankle && hip.visibility > 0.6 && knee.visibility > 0.6 && ankle.visibility > 0.6;

        if (!allVisible) {
          setTooClose(true);
          canvasCtx.restore();
          prevLandmarksRef.current = { hip: null, knee: null, ankle: null };
          return;
        }

        setTooClose(false);

        canvasCtx.save();
        canvasCtx.beginPath();
        canvasCtx.moveTo(hip.x * width, hip.y * height);
        canvasCtx.lineTo(knee.x * width, knee.y * height);
        canvasCtx.lineTo(ankle.x * width, ankle.y * height);
        canvasCtx.strokeStyle = "rgba(21, 169, 207, 0.85)";
        canvasCtx.lineWidth = 7;
        canvasCtx.stroke();

        [hip, knee, ankle].forEach((pt) => {
          canvasCtx.beginPath();
          canvasCtx.arc(pt.x * width, pt.y * height, 10, 0, 2 * Math.PI);
          canvasCtx.fillStyle = "#FC6448";
          canvasCtx.shadowColor = "#FC6448";
          canvasCtx.shadowBlur = 8;
          canvasCtx.fill();
          canvasCtx.shadowBlur = 0;
        });

        const rawAngle = calculateAngle(hip, knee, ankle);

        const smoothed = filterRef.current.filter(rawAngle, performance.now());

        angleBuffer.current.push(smoothed);
        if (angleBuffer.current.length > BUFFER_SIZE) {
          angleBuffer.current.shift();
        }

        const sorted = [...angleBuffer.current].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        const median = sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;

        const rounded = Math.round(median);

        setAngle(rounded);

        canvasCtx.fillStyle = "white";
        canvasCtx.font = "bold 30px Arial";
        canvasCtx.strokeStyle = "black";
        canvasCtx.lineWidth = 4;
        canvasCtx.strokeText(`${rounded}°`, knee.x * width + 20, knee.y * height - 20);
        canvasCtx.fillText(`${rounded}°`, knee.x * width + 20, knee.y * height - 20);

        canvasCtx.restore();
      };

      const videoElement = videoRef.current;
      const canvasElement = canvasRef.current;
      const canvasCtx = canvasElement.getContext("2d");

      const resizeCanvas = () => {
        canvasElement.width = window.innerWidth;
        canvasElement.height = window.innerHeight;
      };

      window.addEventListener("resize", resizeCanvas);
      resizeCanvas();

      const calculateAngle = (hip, knee, ankle) => {
        const getVec = (a, b) => ({
          x: a.x - b.x,
          y: a.y - b.y,
          z: (a.z ?? 0) - (b.z ?? 0),
        });
        const v1 = getVec(hip, knee);
        const v2 = getVec(ankle, knee);

        const dot = v1.x * v2.x + v1.y * v2.y + (v1.z ?? 0) * (v2.z ?? 0);
        const mag1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y + (v1.z ?? 0) * (v1.z ?? 0));
        const mag2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y + (v2.z ?? 0) * (v2.z ?? 0));
        let angleRad = Math.acos(Math.max(-1, Math.min(1, dot / (mag1 * mag2 + 1e-6))));
        let angleDeg = (angleRad * 180) / Math.PI;
        if (angleDeg > 180) angleDeg = 360 - angleDeg;
        return 180 - angleDeg;
      };

      camera = new window.Camera(videoElement, {
        onFrame: async () => {
          if (cameraDeleted || poseDeleted) return;
          try {
            await pose.send({ image: videoElement });
          } catch (err) {
            if (
              err &&
              typeof err.message === "string" &&
              err.message.includes("Cannot pass deleted object as a pointer of type SolutionWasm*")
            ) {
              // Swallow error, pose is deleted
            } else {
              console.error(err);
            }
          }
        },
        width: 1280,
        height: 720,
      });

      pose.onResults(onResults);

      camera.start();
    } catch (err) {
      if (
        err &&
        typeof err.message === "string" &&
        err.message.includes("Cannot pass deleted object as a pointer of type SolutionWasm*")
      ) {
        // Swallow error, pose/camera is deleted
      } else {
        console.error(err);
      }
    }

    return () => {
      window.removeEventListener("resize", () => {});
      if (camera) {
        try {
          cameraDeleted = true;
          camera.stop();
        } catch (err) {
          // Ignore errors on stop
        }
      }
      if (pose) {
        try {
          poseDeleted = true;
          pose.close();
        } catch (err) {
          // Ignore errors on close
        }
      }
      prevLandmarksRef.current = { hip: null, knee: null, ankle: null };
    };
  }, [whichKnee]);

  const captureScreenshot = () => {
    const bottomData = document.querySelector(".bottomData");
    const sideData = document.querySelector(".sideData");
    const captureCircleOuter = bottomData?.querySelector(".capture-circle-outer");

    if (bottomData) {
      bottomData.style.background = "linear-gradient(to right, #FC6448, #FFF)";
      bottomData.style.transition = "background 2s ease-in-out";
    }
    if (captureCircleOuter) captureCircleOuter.style.display = "none";

    let countdown = 11;
    const countdownInterval = setInterval(() => {
      setIsCountdown(true);
      if (countdown > 0) {
        setProgress(countdown);
        countdown--;
      } else {
        clearInterval(countdownInterval);

        const container = document.querySelector(".canvas-container");
        const canvasEl = container.querySelector("canvas");
        const width = canvasEl.width;
        const height = canvasEl.height;

        const { hip, knee, ankle } = prevLandmarksRef.current || {};

        if (!hip || !knee || !ankle) {
          setNoDetection(true);
          if (bottomData) {
            bottomData.style.background = "#ffffffb2";
            if (captureCircleOuter) captureCircleOuter.style.display = "block";
          }
          return;
        }

        if (sideData) sideData.style.display = "none";
        if (bottomData) bottomData.style.display = "none";

        const pts = [
          { x: hip.x * width, y: hip.y * height },
          { x: knee.x * width, y: knee.y * height },
          { x: ankle.x * width, y: ankle.y * height },
        ];

        let minX = Math.min(...pts.map((p) => p.x));
        let maxX = Math.max(...pts.map((p) => p.x));
        let minY = Math.min(...pts.map((p) => p.y));
        let maxY = Math.max(...pts.map((p) => p.y));

        const padding = 250;
        minX = Math.max(minX - padding, 0);
        minY = Math.max(minY - padding, 0);
        maxX = Math.min(maxX + padding, width);
        maxY = Math.min(maxY + padding, height);

        let cropX = minX;
        let cropY = minY;
        let cropW = maxX - minX;
        let cropH = maxY - minY;

        if (cropX + cropW > width) cropW = width - cropX;
        if (cropY + cropH > height) cropH = height - cropY;

        html2canvas(container, {
          x: cropX,
          y: cropY,
          width: cropW,
          height: cropH,
          scale: window.devicePixelRatio,
          backgroundColor: "#000",
        }).then((canvas) => {
          const targetW = 400;
          const targetH = 600;

          const finalCanvas = document.createElement("canvas");
          finalCanvas.width = targetW;
          finalCanvas.height = targetH;
          const ctx = finalCanvas.getContext("2d");

          const zoomFactor = 1.5;
          const scale = Math.min(targetW / canvas.width, targetH / canvas.height) * zoomFactor;

          const newW = canvas.width * scale;
          const newH = canvas.height * scale;
          const offsetX = (targetW - newW) / 2;
          const offsetY = (targetH - newH) / 2;

          const pattern = ctx.createPattern(canvas, "repeat");
          ctx.fillStyle = pattern;
          ctx.fillRect(0, 0, targetW, targetH);

          ctx.drawImage(canvas, offsetX, offsetY, newW, newH);

          const lastAngleDetected = document.querySelector("span.angle")?.textContent || "0°";
          const image = finalCanvas.toDataURL("image/png");

          setScreenshot(image);
          console.log("Last detected angle:", lastAngleDetected);
          secureLocalStorage.setItem("last_angle_detected", lastAngleDetected);

          if (sideData) sideData.style.display = "";
          if (bottomData) bottomData.style.display = "";
          if (bottomData) bottomData.style.background = "#ffffffb2";
          if (captureCircleOuter) captureCircleOuter.style.display = "block";
        });
      }

      const countdownDisplay = [];
      for (let i = countdown; i > countdown - 5 && i > 0; i--) {
        countdownDisplay.push(
          <span key={i} className={i === countdown ? "active" : ""}>
            {i}
          </span>
        );
      }
      setProgress(countdownDisplay);
    }, 1000);
  };

  const handleSocialShare = async (platform) => {
    if (shareUploading) return;

    if (shareSignedUrl || shareImageUrl) {
      openSocialShare(platform, shareSignedUrl || shareImageUrl);
      return;
    }

    setShareUploading(true);

    try {
      const container = document.querySelector(".share-image-container");
      const canvas = await html2canvas(container, {
        x: 0,
        y: 0,
        width: container.clientWidth,
        height: container.clientHeight,
        scale: window.devicePixelRatio,
      });

      const image = canvas.toDataURL("image/png");
      const patientId = secureLocalStorage.getItem("patient_id");
      const timestamp = new Date().toISOString().replace(/[:.-]/g, "");
      const fileName = `share/shareable-${patientId}-${timestamp}.png`;

      const params = {
        Bucket: "engivio-rom-uploads",
        Key: fileName,
        Body: Uint8Array.from(
          atob(image.replace(/^data:image\/\w+;base64,/, "")),
          (c) => c.charCodeAt(0)
        ),
        ContentEncoding: "base64",
        ContentType: "image/png",
      };

      const data = await s3.upload(params).promise();
      const s3Url = data.Location;

      setShareImageUrl(s3Url);

      openSocialShare(platform, s3Url, image);
    } catch (error) {
      if (
        error &&
        typeof error.message === "string" &&
        error.message.includes("Cannot pass deleted object as a pointer of type SolutionWasm*")
      ) {
        // Swallow error, do not propagate
      } else {
        console.error("Error uploading to S3 or generating signed URL:", error);
      }
    } finally {
      setShareUploading(false);
    }
  };

  const FB_APP_ID = process.env.NEXT_PUBLIC_FB_APP_ID || "229701104963045";

  const openPopup = (url, name = "_blank", width = 600, height = 400) => {
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    window.open(
      url,
      name,
      `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars=yes,status=1`
    );
  };

  const openSocialShare = (platform, shareUrl, imageDataUrl) => {
    const shareText = "Check out my ROM progress!";
    const redirectUri = `${window.location.origin}/messenger-close`;
    const shareUrlFull = `https://dashboard.engiv.io/share?image=${encodeURIComponent(shareUrl)}`;

    switch (platform) {
      case "facebook": {
        const quote = "My knee ROM just got better! See my progress with Engivio 🚀";
        const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(quote)}`;
        openPopup(url, "fbShareDialog");
        break;
      }

      case "messenger": {
        const url = `https://www.facebook.com/dialog/send?app_id=${encodeURIComponent(FB_APP_ID)}&link=${encodeURIComponent(shareUrl)}&redirect_uri=${encodeURIComponent(shareUrl)}&display=popup`;
        openPopup(url, "messengerShareDialog");
        break;
      }

      case "whatsapp": {
        const url = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
        openPopup(url, "whatsappShareDialog");
        break;
      }

      case "tiktok": {
        if (imageDataUrl) {
          const a = document.createElement("a");
          a.href = imageDataUrl;
          a.download = "rom-progress.png";
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
        openPopup("https://www.tiktok.com/upload", "tiktokUpload", 900, 700);
        break;
      }

      case "x":
      case "twitter": {
        const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrlFull)}&text=${encodeURIComponent(shareText)}`;
        openPopup(url, "twitterShareDialog");
        break;
      }

      case "copy": {
        navigator.clipboard
          .writeText(shareUrlFull)
          .then(() => {
            alert("Link copied to clipboard.");
          })
          .catch((err) => {
            console.error("Failed to copy: ", err);
          });
        break;
      }

      case "instagram": {
        if (!imageDataUrl) {
          alert("No image to share.");
          break;
        }
        const a = document.createElement("a");
        a.href = imageDataUrl;
        a.download = "rom-progress.png";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.location.href = "instagram://story-camera";
        break;
      }

      default:
        openPopup(shareUrl, "defaultShareDialog");
    }
  };

  const handleBackgroundColorChange = (color) => {
    setBackgroundColor(color);
    setShareImageUrl(null);
    setShareSignedUrl(null);
  };

  return (
    <div
      className="canvas-container"
      style={{ position: "fixed", inset: 0, overflow: "hidden" }}
    >
      <video
        ref={videoRef}
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: "scaleX(-1)",
          display: "none",
        }}
        muted
        playsInline
      />
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
         // transform: "scaleX(-1)",
        }}
      />

      {/* ROM Angle Display */}
      <div className="sideData">
        <div className="romAngle">
          <p>
            Angle: <span className="angle">{angle !== null ? angle : 0}°</span>
          </p>
        </div>
        <div className="romMovement">
          <img
            src={getImagePath(angle)}
            style={{ transition: "opacity 0.5s ease-in-out" }}
            alt="Knee Movement"
          />
        </div>
      </div>

      {/* Modal - If no MediaPose areas detected */}
      {tooClose && (
        <div className="no-joints-detected">
          Move slightly farther or adjust camera — no joints detected
        </div>
      )}

      {/* Modal - Capturing Screenshot */}

      {screenshot && (
        <div className="screenshot-modal">
          <div className="screenshot-container">
            <div className="screenshot-image-container">
              <img src={screenshot} alt="Screenshot Preview" />
            </div>
            <div className="screenshot-options">
              <button
                className="modal-save"
                onClick={() => {
                  setScreenshot(null);
                  setImageSaved(true);
                  saveData().then(() => {
                    setImageSaved(false);
                    setShowSaveModal(true);
                  });
                }}
              >
                Save
              </button>
              <button
                className="modal-close"
                onClick={() => setScreenshot(null)}
              >
                Take another one
              </button>
              <button
                className="modal-share"
                onClick={() => {
                  setScreenshot(screenshot);
                  setShareScreenshot(true);
                  // Reset share image and URL when opening share modal
                  setShareImageUrl(null);
                  setShareSignedUrl(null);
                }}
              >
                Share
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Sharing */}

      {shareScreenshot && (
        //  Captured Image BG
        <div className="share-screenshot-modal">
          <div className="share-screenshot-container">
            {isScreenshotBG ? (
              <div
                className="share-image-container with-rom-bg"
                style={{
                  backgroundImage: backgroundColor,
                  color: "#15A9CF",
                  backgroundPosition: "right",
                  backgroundSize: "cover",
                  backgroundRepeat: "no-repeat",
                }}
              >
                <div className="with-rom-bg-overlay"></div>
                <div
                  className="share-image-content"
                  style={{ background: "transparent" }}
                >
                  <div className="with-bg-content">
                    <div class="top-label-container">
                      <img
                        src="../engivio-logo-white.svg"
                        style={{ width: "50px" }}
                      />
                      <p className="top-label">
                        {secureLocalStorage.getItem("week_no")} weeks{" "}
                        {surgeryStatus}
                      </p>
                    </div>
                    <div class="bottom-label-container">
                      {/* <div className="left-bottom-label-container">
                        <p className="label">Range of Motion</p>
                        <h3>
                          <img
                            src="../angle-white.svg"
                            style={{ width: "20px" }}
                          />
                          {secureLocalStorage.getItem("last_angle_detected")}
                        </h3>
                      </div> */}
                      <div className="left-bottom-label-container">
                        <p className="label">Next Knee Flexion Goal</p>
                        <h3>
                          {" "}
                          <img
                            src="../goal-white.svg"
                            style={{ width: "20px" }}
                          />
                          {(() => {
                            const angleRaw = secureLocalStorage.getItem(
                              "last_angle_detected"
                            );
                            const angle = angleRaw
                              ? parseInt(angleRaw.replace("°", ""), 10)
                              : null;

                            if (angle == null) return null;
                            if (angle < 65) return "65°";
                            if (angle >= 65 && angle <= 89) return "90°";
                            if (angle >= 90 && angle <= 109) return "110°";
                            if (angle >= 110 && angle <= 119) return "120°";
                            if (angle >= 120) return "You did it! 🎉";
                          })()}
                        </h3>
                        <p className="img-rom-result">
                          {(() => {
                            const angleRaw = secureLocalStorage.getItem(
                              "last_angle_detected"
                            );
                            const angle = angleRaw
                              ? parseInt(angleRaw.replace("°", ""), 10)
                              : null;

                            if (angle == null) return "No angle detected yet.";
                            if (angle < 65)
                              return "Every bend counts! Keep going toward 65° to unlock safer walking.";
                            if (angle >= 65 && angle <= 89)
                              return "Close to a big milestone! Once you hit 90°, sitting and walking will feel much easier.";
                            if (angle >= 90 && angle <= 109)
                              return "Getting there! 110° = smoother stairs, easier car rides, and getting back on the bike.";
                            if (angle >= 110 && angle <= 119)
                              return "One milestone away: at 120°, daily life will start feeling natural again!";
                            if (angle >= 120)
                              return "120° is the standard most surgeons look for. Anything beyond is a bonus (i.e. sports)!";
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="share-image-container"
                style={{
                  background: backgroundColor,
                  color: "#15A9CF",
                  backgroundPosition: "right",
                  backgroundSize: "cover",
                }}
              >
                <div className="share-image-content">
                  <p className="week-label">
                    {secureLocalStorage.getItem("week_no")} weeks{" "}
                    {surgeryStatus}
                  </p>
                  <h2>My Range of Motion</h2>
                  <h3>
                    <img src="../angle-blue.svg" style={{ width: "20px" }} />
                    {"  "}
                    {secureLocalStorage.getItem("last_angle_detected")}
                  </h3>
                  <h2>Next Knee Flexion Goal:</h2>
                  <h3>
                    <img src="../goal-blue.svg" style={{ width: "20px" }} />
                    {"  "}
                    {(() => {
                      const angleRaw = secureLocalStorage.getItem(
                        "last_angle_detected"
                      );
                      const angle = angleRaw
                        ? parseInt(angleRaw.replace("°", ""), 10)
                        : null;

                      if (angle == null) return null;
                      if (angle < 65) return "65°";
                      if (angle >= 65 && angle <= 89) return "90°";
                      if (angle >= 90 && angle <= 109) return "110°";
                      if (angle >= 110 && angle <= 119) return "120°";
                      if (angle >= 120) return "You did it! 🎉";
                    })()}
                  </h3>
                  <p className="rom-result">
                    {(() => {
                      const angleRaw = secureLocalStorage.getItem(
                        "last_angle_detected"
                      );
                      const angle = angleRaw
                        ? parseInt(angleRaw.replace("°", ""), 10)
                        : null;

                      if (angle == null) return "No angle detected yet.";
                      if (angle < 65)
                        return "Every bend counts! Keep going toward 65° to unlock safer walking.";
                      if (angle >= 65 && angle <= 89)
                        return "Close to a big milestone! Once you hit 90°, sitting and walking will feel much easier.";
                      if (angle >= 90 && angle <= 109)
                        return "Getting there! 110° = smoother stairs, easier car rides, and getting back on the bike.";
                      if (angle >= 110 && angle <= 119)
                        return "One milestone away: at 120°, daily life will start feeling natural again!";
                      if (angle >= 120)
                        return "120° is the standard most surgeons look for. Anything beyond is a bonus (i.e. sports)!";
                    })()}
                  </p>
                  <img
                    className="engivio-logo"
                    src="../engivio-hd.png"
                    alt="Engivio Logo"
                    style={{
                      width: "40px ",
                      height: "auto",
                      marginTop: "20px",
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="color-buttons">
            <h4>Customize:</h4>
            <button
              className={
                backgroundColor === `url(${screenshot})` ? "active" : ""
              }
              style={{
                backgroundImage: `url(${screenshot})`,
                backgroundSize: "cover",
                backgroundPosition: "right",
                border:
                  backgroundColor === `url(${screenshot})`
                    ? "2px solid #fff"
                    : "none",
              }}
              onClick={() => {
                setIsScreenshotBG(true);
                handleBackgroundColorChange(`url(${screenshot})`);
              }}
            ></button>
            <button
              className={backgroundColor === "#CAEEF9" ? "active" : ""}
              style={{
                backgroundColor: "#CAEEF9",
                border:
                  backgroundColor === "#CAEEF9" ? "2px solid #15b1d7" : "none",
              }}
              onClick={() => {
                handleBackgroundColorChange("#CAEEF9");
                setIsScreenshotBG(false);
              }}
            ></button>
            <button
              className={backgroundColor === "#1E1E1E" ? "active" : ""}
              style={{
                backgroundColor: "#1E1E1E",
                border:
                  backgroundColor === "#1E1E1E" ? "2px solid #fff" : "none",
              }}
              onClick={() => {
                handleBackgroundColorChange("#1E1E1E");
                setIsScreenshotBG(false);
              }}
            ></button>
            <button
              className={backgroundColor === "#15A9CF" ? "active" : ""}
              style={{
                backgroundColor: "#15A9CF",
                border:
                  backgroundColor === "#15A9CF" ? "2px solid #fff" : "none",
              }}
              onClick={() => {
                handleBackgroundColorChange("#15A9CF");
                setIsScreenshotBG(false);
              }}
            ></button>
            <button
              className={backgroundColor === "#FC6448" ? "active" : ""}
              style={{
                backgroundColor: "#FC6448",
                border:
                  backgroundColor === "#FC6448" ? "2px solid #fff" : "none",
              }}
              onClick={() => {
                handleBackgroundColorChange("#FC6448");
                setIsScreenshotBG(false);
              }}
            ></button>
          </div>
          <div className="social-icons">
            <img
              src="https://cdn.jsdelivr.net/npm/simple-icons@v5/icons/facebook.svg"
              alt="Facebook"
              onClick={() => handleSocialShare("facebook")}
              style={{
                opacity: shareUploading ? 0.5 : 1,
                pointerEvents: shareUploading ? "none" : "auto",
              }}
            />

            <img
              src="https://cdn.jsdelivr.net/npm/simple-icons@v5/icons/messenger.svg"
              alt="Messenger"
              onClick={() => handleSocialShare("messenger")}
              style={{
                opacity: shareUploading ? 0.5 : 1,
                pointerEvents: shareUploading ? "none" : "auto",
              }}
            />
            <img
              src="https://cdn.jsdelivr.net/npm/simple-icons@v5/icons/twitter.svg"
              alt="X"
              onClick={() => handleSocialShare("x")}
              style={{
                opacity: shareUploading ? 0.5 : 1,
                pointerEvents: shareUploading ? "none" : "auto",
              }}
            />
            <img
              src="https://cdn.jsdelivr.net/npm/simple-icons@v5/icons/whatsapp.svg"
              alt="Whatsapp"
              onClick={() => handleSocialShare("whatsapp")}
              style={{
                opacity: shareUploading ? 0.5 : 1,
                pointerEvents: shareUploading ? "none" : "auto",
              }}
            />
            <img
              src="https://cdn.jsdelivr.net/npm/feather-icons@4.29.0/dist/icons/copy.svg"
              alt="Copy"
              onClick={() => handleSocialShare("copy")}
              style={{
                opacity: shareUploading ? 0.5 : 1,
                pointerEvents: shareUploading ? "none" : "auto",
              }}
            />
          </div>
        </div>
      )}
      {/* Modal - Tips */}
      {/* {tips && (
        <div className="show-save-modal">
          <div className="modal-content">
            <h2>
              <b>🚀 Quick Tip </b>
            </h2>
            <h3>
              For the best ROM experience, please sit facing your <b>{whichKnee}</b> side toward the
              camera. This helps us capture your movement clearly. You got this!
            </h3>
            <button className="modal-save" onClick={() => setTips(false)}>
            👉 I’m ready, Let’s Go!
            </button>
          </div>
        </div>
      )} */}

      {tips && whichKnee && (
        <div
          className="direction-container"
        >
          <img
            className={`arrow-direction-${whichKnee.toLowerCase()}`}
            src={`../arrow-direction-${whichKnee.toLowerCase()}.svg`}
            alt="Capture Icon"
          /><br/>
          <h3 className="direction-text">
            {whichKnee === "Left"
              ? <span>Face to your right to check your <b>LEFT</b> knee flexion.</span>
              : <span>Face to your left to check your <b>RIGHT</b> knee flexion.</span>}
          </h3>
        </div>
      )}


      {/* Modal - Loading (Image is saving) */}
      {imageSaved && (
        <div className="show-save-modal">
          <div className="modal-content">
            <h2>Please wait...</h2>
          </div>
        </div>
      )}

      {/* Modal - No Detection */}

      {noDetection && (
        <div className="show-save-modal">
          <div className="modal-content">
            <h2>
              <b>Oops! </b>
            </h2>
            <h3>
              Looks like we missed it—sit tall, turn to the side, and let’s
              capture this!
            </h3>
            <button
              className="modal-save"
              onClick={() => setNoDetection(false)}
            >
              👉 Try again!
            </button>
          </div>
        </div>
      )}

      {/* Modal - Final Save */}

      {showSaveModal && (
        <div className="show-save-modal">
          <div className="modal-content">
            <button
              className="modal-close"
              onClick={() => setShowSaveModal(false)}
            >
              &times;
            </button>
            <h2>Great Job!</h2>
            {updateSavedText(secureLocalStorage.getItem("last_angle_detected"))}
            <p>
              You've reached{" "}
              <b>{secureLocalStorage.getItem("last_angle_detected")}</b> today.
            </p>
          </div>
        </div>
      )}

      <div className="bottomData fade3">
        <div className="countdown-container">
          <h3 className="countdown-number">{isCountdown && progress}</h3>
        </div>
        <div className="capture-circle-outer" onClick={captureScreenshot}>
          <div className="capture-circle-inner">
            <img
              className="camera-capture"
              src="../captureIcon.png"
              alt="Capture Icon"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RomMN;
